import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ReactComponent as OccasiaLogoIcon } from '../icons/occasia-logo-icon.svg';
import { API_BASE_URL } from '../../config/apiEndpoints';
import './Landing2Footer.css';

const FOOTER_GRID_GAP = 22;
const FOOTER_GRID_RADIUS = 3.2;
const FOOTER_GRID_BURST_MS = 200;
const FOOTER_GRID_HOLD = 1.05;
const FOOTER_GRID_RING = [
  { max: 0.85, color: '#046242' },
  { max: 1.85, color: '#BED347' },
  { max: FOOTER_GRID_RADIUS, color: '#E4FD58' },
];

const NEWSLETTER_API = `${API_BASE_URL}/newsletter/subscribe`;

export const useBrandDotGrid = (gridRef) => {
  useEffect(() => {
    const canvas = gridRef.current;
    const wrap = canvas?.parentElement;
    if (!canvas || !wrap) return undefined;

    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const clusters = [];
    let cols = 0;
    let rows = 0;
    let frameId = 0;
    let lastBurst = 0;

    const spawnBurst = () => {
      const count = 3 + Math.floor(Math.random() * 2);
      const margin = Math.ceil(FOOTER_GRID_RADIUS) + 1;
      for (let i = 0; i < count; i += 1) {
        clusters.push({
          col: margin + Math.floor(Math.random() * Math.max(1, cols - margin * 2)),
          row: margin + Math.floor(Math.random() * Math.max(1, rows - margin * 2)),
          born: performance.now(),
        });
      }
    };

    const ringColor = (distance) => {
      const ring = FOOTER_GRID_RING.find((item) => distance <= item.max);
      return ring?.color ?? '#E4FD58';
    };

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const width = wrap.clientWidth;
      const height = wrap.clientHeight;
      canvas.width = Math.max(1, Math.floor(width * dpr));
      canvas.height = Math.max(1, Math.floor(height * dpr));
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cols = Math.ceil(width / FOOTER_GRID_GAP);
      rows = Math.ceil(height / FOOTER_GRID_GAP);
      clusters.length = 0;
      lastBurst = 0;
    };

    const draw = (now) => {
      const width = wrap.clientWidth;
      const height = wrap.clientHeight;
      ctx.clearRect(0, 0, width, height);

      if (!reduced && now - lastBurst >= FOOTER_GRID_BURST_MS) {
        spawnBurst();
        lastBurst = now;
      }

      const hold = FOOTER_GRID_HOLD * 1000;
      for (let i = clusters.length - 1; i >= 0; i -= 1) {
        if (now - clusters[i].born >= hold) clusters.splice(i, 1);
      }

      const lit = new Map();
      clusters.forEach((cluster) => {
        const intensity = Math.sin(((now - cluster.born) / hold) * Math.PI);
        const extent = Math.ceil(FOOTER_GRID_RADIUS);
        for (let dy = -extent; dy <= extent; dy += 1) {
          for (let dx = -extent; dx <= extent; dx += 1) {
            const distance = Math.hypot(dx, dy);
            if (distance > FOOTER_GRID_RADIUS) continue;
            const key = `${cluster.col + dx},${cluster.row + dy}`;
            const prev = lit.get(key);
            if (!prev || intensity > prev.intensity) {
              lit.set(key, { color: ringColor(distance), intensity });
            }
          }
        }
      });

      for (let row = 0; row < rows; row += 1) {
        for (let col = 0; col < cols; col += 1) {
          const litDot = lit.get(`${col},${row}`);
          ctx.beginPath();
          ctx.arc(
            col * FOOTER_GRID_GAP,
            row * FOOTER_GRID_GAP,
            1.2,
            0,
            Math.PI * 2
          );
          if (litDot) {
            ctx.globalAlpha = 0.12 + litDot.intensity * 0.88;
            ctx.fillStyle = litDot.color;
          } else {
            ctx.globalAlpha = 1;
            ctx.fillStyle = 'rgba(4, 98, 66, 0.14)';
          }
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;
      if (!reduced) frameId = window.requestAnimationFrame(draw);
    };

    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(wrap);
    if (reduced) {
      draw(performance.now());
    } else {
      frameId = window.requestAnimationFrame(draw);
    }
    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(frameId);
    };
  }, [gridRef]);
};

const Landing2Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const gridRef = useRef(null);
  useBrandDotGrid(gridRef);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(NEWSLETTER_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), update_enabled: false }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="l2-footer">
      <div className="l2-footer-glow" aria-hidden />
      <div className="l2-footer-grid" aria-hidden>
        <canvas ref={gridRef} className="l2-footer-grid-dots" />
      </div>

      <div className="l2-footer-banner">
        <div className="l2-footer-banner-col">
          <h2 className="l2-footer-banner-title">
            <span className="l2-footer-banner-line">Connecting communities </span>
            <span className="l2-footer-banner-highlight">through unique spaces.</span>
          </h2>

          <div className="l2-footer-banner-subscribe">
            <p className="l2-footer-banner-subscribe-copy">
              We keep track of cool events at our vetted venues.
              <br />
              Join the mailing list and we&apos;ll send the best ones to your inbox.
            </p>
            <form className="l2-footer-subscribe-form" onSubmit={handleSubscribe} noValidate>
              <input
                type="email"
                className="l2-footer-email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting || submitStatus === 'success'}
                aria-label="Email"
              />
              <button
                type="submit"
                className={`l2-footer-submit ${submitStatus === 'success' ? 'l2-footer-submit--done' : ''}`}
                disabled={isSubmitting || submitStatus === 'success'}
              >
                {isSubmitting ? '...' : submitStatus === 'success' ? 'Joined' : 'Submit'}
              </button>
            </form>
            {submitStatus === 'success' && (
              <p className="l2-footer-status l2-footer-status--success">Subscribed successfully!</p>
            )}
            {submitStatus === 'error' && (
              <p className="l2-footer-status l2-footer-status--error">Subscription failed. Please try again.</p>
            )}
          </div>
        </div>

        <div className="l2-footer-banner-col l2-footer-banner-col--end">
          <h2 className="l2-footer-banner-title l2-footer-banner-title--end">
            human by
            <br />
            design
          </h2>
        </div>
      </div>

      <div className="l2-footer-card">
        <div className="l2-footer-card-body">
        <div className="l2-footer-nav-row">
          <div className="l2-footer-secondary">
            <Link to="/" className="l2-footer-logo" aria-label="Occasia">
              <OccasiaLogoIcon className="l2-footer-logo-svg" />
            </Link>
            <nav className="l2-footer-secondary-links" aria-label="Footer secondary">
              <Link to="/privacy">Privacy Policy</Link>
              <Link to="/terms">Terms of use</Link>
              <a href="https://www.instagram.com/occasia.ofcl/" target="_blank" rel="noopener noreferrer">Instagram</a>
              <a href="https://www.linkedin.com/company/occasia/about/" target="_blank" rel="noopener noreferrer">LinkedIn</a>
            </nav>
          </div>

          <nav className="l2-footer-primary" aria-label="Footer">
            <Link to="/">For organisers</Link>
            <Link to="/onboard?persona=venues">For venues</Link>
            <Link to="/about">About</Link>
            <Link to="/blog">Journal</Link>
            <a href="mailto:support@occasia.io">Contact us</a>
          </nav>
        </div>

        <div className="l2-footer-legal-block">
          <p className="l2-footer-disclaimer">
            Occasia is built by a small team that genuinely cares about making venue booking less painful.
            <br />
            <br />
            If you have a question, a venue to list, or just want to share feedback, drop us a line.
            <br />
            We read everything and we&apos;ll get back to you.
            <br />
            <span className="l2-footer-reach">
              Reach us at <a href="mailto:support@occasia.io">support@occasia.io</a>
            </span>
          </p>
          <p className="l2-footer-copyright">
            © 2026 Occasia
          </p>
        </div>
        </div>
      </div>
    </footer>
  );
};

export default Landing2Footer;
